(() => {
  const cardSelector = '.stwc__img-wrap[data-hover-src], .stwfp__image-wrap[data-hover-src]';
  const desktopHover = window.matchMedia('(hover: hover) and (pointer: fine)');

  const observed = new WeakSet();
  const queued = new WeakSet();
  const loading = new WeakSet();
  const queue = [];
  const maxConcurrent = 2;
  let active = 0;
  let io;

  const mountCardImage = (wrapper, image) => {
    if (!wrapper.isConnected || wrapper.dataset.hoverReady === 'true') return;

    image.className = wrapper.classList.contains('stwfp__image-wrap')
      ? 'stwfp__image-secondary'
      : 'stwc__img-secondary';
    image.alt = '';
    image.setAttribute('aria-hidden', 'true');
    image.decoding = 'async';
    image.fetchPriority = 'low';
    image.width = 720;
    image.height = 520;

    wrapper.appendChild(image);
    wrapper.dataset.hoverReady = 'true';
    wrapper.removeAttribute('data-hover-loading');
  };

  const startCardLoad = (wrapper, priority = 'low', counted = false) => {
    if (!wrapper || loading.has(wrapper) || wrapper.dataset.hoverReady === 'true') return;

    const src = wrapper.dataset.hoverSrc;
    if (!src) return;

    loading.add(wrapper);
    wrapper.dataset.hoverLoading = 'true';

    const image = new Image();
    image.decoding = 'async';
    image.fetchPriority = priority;

    const finish = () => {
      if (counted) {
        active = Math.max(0, active - 1);
        pump();
      }
    };

    image.onload = () => {
      mountCardImage(wrapper, image);
      finish();
    };

    image.onerror = () => {
      wrapper.removeAttribute('data-hover-loading');
      finish();
    };

    image.src = src;
  };

  const pump = () => {
    while (active < maxConcurrent && queue.length) {
      const wrapper = queue.shift();

      if (
        !wrapper ||
        !wrapper.isConnected ||
        wrapper.dataset.hoverReady === 'true' ||
        loading.has(wrapper)
      ) {
        continue;
      }

      active += 1;
      startCardLoad(wrapper, 'low', true);
    }
  };

  const enqueue = (wrapper) => {
    if (
      !wrapper ||
      queued.has(wrapper) ||
      loading.has(wrapper) ||
      wrapper.dataset.hoverReady === 'true'
    ) {
      return;
    }

    queued.add(wrapper);
    queue.push(wrapper);
    pump();
  };

  const scan = (root = document) => {
    const wrappers =
      root.matches?.(cardSelector)
        ? [root]
        : root.querySelectorAll?.(cardSelector) || [];

    wrappers.forEach((wrapper) => {
      if (observed.has(wrapper)) return;
      observed.add(wrapper);
      io?.observe(wrapper);
    });
  };

  const initProductCards = () => {
    if (!desktopHover.matches) return;

    io = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (!entry.isIntersecting) return;
          io.unobserve(entry.target);
          enqueue(entry.target);
        });
      },
      { rootMargin: '500px 0px' }
    );

    scan(document);

    const mo = new MutationObserver((mutations) => {
      mutations.forEach((mutation) => {
        mutation.addedNodes.forEach((node) => {
          if (node.nodeType === 1) scan(node);
        });
      });
    });

    mo.observe(document.body, { childList: true, subtree: true });

    document.addEventListener('mouseover', (event) => {
      const wrapper = event.target.closest?.(cardSelector);
      if (!wrapper) return;

      const from = event.relatedTarget;
      if (from && wrapper.contains(from)) return;

      if (wrapper.dataset.hoverReady !== 'true' && !loading.has(wrapper)) {
        startCardLoad(wrapper, 'high', false);
      }
    });
  };

  const initProductThumbHover = () => {
    if (!desktopHover.matches) return;

    let timer = null;

    document.addEventListener('mouseover', (event) => {
      const thumb = event.target.closest?.('.product-media--thumb');
      if (!thumb || thumb.getAttribute('aria-current') === 'true') return;

      const from = event.relatedTarget;
      if (from && thumb.contains(from)) return;

      clearTimeout(timer);
      timer = setTimeout(() => {
        if (thumb.matches(':hover')) thumb.click();
      }, 80);
    });

    document.addEventListener('mouseout', (event) => {
      const thumb = event.target.closest?.('.product-media--thumb');
      if (!thumb) return;

      const to = event.relatedTarget;
      if (to && thumb.contains(to)) return;

      clearTimeout(timer);
      timer = null;
    });
  };

  const init = () => {
    initProductCards();
    initProductThumbHover();
  };

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init, { once: true });
  } else {
    init();
  }
})();